import { motion } from 'framer-motion';
import { useState } from 'react';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { sendInvestmentInquiryEmail } from '../services/emailService';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    investmentAmount: '',
    preferredPlan: 'standard'
  });

  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    // Optionally, add validation here

    const result = await sendInvestmentInquiryEmail(formData);

    if (result.success) {
      setStatus({ type: 'success', message: 'Your inquiry has been sent! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        investmentAmount: '',
        preferredPlan: 'standard'
      });
    } else {
      setStatus({ type: 'error', message: 'There was an error sending your inquiry. Please try again later.' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: "Investment Hotline",
      details: ["+1 (831) 220-9723"],
      description: "24/7 Priority Support for Investors"
    },
    {
      icon: EnvelopeIcon,
      title: "Email Support",
      details: ["support@wealthquota.com"],
      description: "Response within 2 hours for investment inquiries"
    },
    {
      icon: MapPinIcon,
      title: "Investment Office",
      details: ["350 S Figueroa St, Los Angeles, CA 900711"],
      description: "Schedule a private consultation"
    },
    {
      icon: ClockIcon,
      title: "Trading Hours",
      details: ["24/7 Online Platform Access", "Office Hours: Mon-Fri 9AM-6PM EST"],
      description: "Investment processing available 24/7"
    }
  ];

  return (
    <div className="min-h-screen bg-background-light">
      <Navigation />
      
      {/* Hero Section - Redesigned with modern gradient */}
      <motion.section 
        className="relative py-24 bg-gradient-to-r from-primary-700 to-primary-500 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRjMC0yIDEtMyAyLTNzMyAxIDMgM2MwIDItMSAzLTMgM3MtMi0xLTItM3ptMC0xN2MwLTIgMS0zIDItM3MzIDEgMyAzYzAgMi0xIDMtMyAzcy0yLTEtMi0zem0xNyAwYzAtMiAxLTMgMi0zczMgMSAzIDNjMCAyLTEgMy0zIDNzLTItMS0yLTN6bTE3IDBjMC0yIDEtMyAyLTNzMyAxIDMgM2MwIDItMSAzLTMgM3MtMi0xLTItM3ptLTE3IDE3YzAtMiAxLTMgMi0zczMgMSAzIDNjMCAyLTEgMy0zIDNzLTItMS0yLTN6bTE3IDBjMC0yIDEtMyAyLTNzMyAxIDMgM2MwIDItMSAzLTMgM3MtMi0xLTItM3ptLTE3IDE3YzAtMiAxLTMgMi0zczMgMSAzIDNjMCAyLTEgMy0zIDNzLTItMS0yLTN6bTE3IDBjMC0yIDEtMyAyLTNzMyAxIDMgM2MwIDItMSAzLTMgM3MtMi0xLTItM3oiLz48L2c+PC9nPjwvc3ZnPg==')] bg-center"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              className="inline-block mb-4 px-4 py-1 rounded-full bg-secondary-500/20 text-secondary-500 font-medium text-sm"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Start Your Financial Journey
            </motion.div>
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Invest Today, Secure <span className="text-secondary-400">Tomorrow</span>
            </motion.h1>
            <motion.p 
              className="mt-4 text-lg sm:text-xl text-primary-100 max-w-2xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Ready to earn up to 20x returns in 21 days? Our investment experts are here to guide you through our high-yield investment plans and help you maximize your wealth potential.
            </motion.p>
          </div>
        </div>
        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
            <path 
              fill="#FFFFFF" 
              fillOpacity="1" 
              d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,224C672,245,768,267,864,250.7C960,235,1056,181,1152,170.7C1248,160,1344,192,1392,208L1440,224L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z">
            </path>
          </svg>
        </div>
      </motion.section>

      <div className="relative bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row gap-16">
            {/* Contact Info Cards - Left Column */}
            <motion.div 
              className="lg:w-1/3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div className="sticky top-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-primary-700 border-b border-primary-100 pb-4 mb-8">Get in Touch</h2>
                
                <div className="space-y-6">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={index}
                      className="bg-primary-50 rounded-xl p-6 border-l-4 border-primary-500 hover:shadow-md transition-shadow"
                      whileHover={{ x: 5 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                    >
                      <div className="flex items-start">
                        <div className="bg-white shadow-sm p-3 rounded-lg mr-4">
                          <item.icon className="h-6 w-6 text-primary-500" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-primary-700 mb-2">{item.title}</h3>
                          {item.details.map((detail, idx) => (
                            <p key={idx} className="text-neutral-700 text-sm mb-1">{detail}</p>
                          ))}
                          <p className="text-sm text-primary-500 mt-2 font-medium">{item.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Map Section - Moved to sidebar */}
                <motion.div 
                  className="mt-8 rounded-xl overflow-hidden shadow-md border border-neutral-200"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
                  <iframe
                    title="Office Location"
                    className="w-full h-[220px]"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3305.7277858239856!2d-118.25439492357272!3d34.05335897311693!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c7b44f6af3d3%3A0x6b08d5939f4e068c!2s350%20S%20Figueroa%20St%2C%20Los%20Angeles%2C%20CA%2090071!5e0!3m2!1sen!2sus!4v1689432234567!5m2!1sen!2sus"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-allowed"
                    allowFullScreen
                  />
                </motion.div>
              </div>
            </motion.div>
            
            {/* Contact Form Section - Right Column */}
            <motion.div 
              className="lg:w-2/3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <div className="bg-white rounded-2xl overflow-hidden">
                <div className="px-6 py-8 bg-primary-600 text-white">
                  <h2 className="text-2xl sm:text-3xl font-bold">Investment Inquiry</h2>
                  <p className="mt-2 text-primary-100">Fill out the form below and our investment advisors will contact you within 2 hours.</p>
            </div>
                
            <motion.form 
              onSubmit={handleSubmit}
                  className="p-6 sm:p-8 space-y-6 border border-neutral-100 rounded-b-2xl shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-medium text-neutral-700">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-all"
                    required
                  />
                </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-sm font-medium text-neutral-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-all"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-neutral-700">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                        className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-all"
                    required
                  />
                </div>
                    <div className="space-y-2">
                      <label htmlFor="investmentAmount" className="block text-sm font-medium text-neutral-700">Investment Amount ($)</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-neutral-500 sm:text-sm">$</span>
                        </div>
                  <input
                    type="number"
                    name="investmentAmount"
                    id="investmentAmount"
                    value={formData.investmentAmount}
                    onChange={handleChange}
                    min="1000"
                          className="block w-full pl-7 rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-all"
                    required
                  />
                </div>
              </div>
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="preferredPlan" className="block text-sm font-medium text-neutral-700">Preferred Investment Plan</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="relative">
                        <input 
                          type="radio" 
                          id="standard" 
                          name="preferredPlan" 
                          value="standard" 
                          className="sr-only peer" 
                          checked={formData.preferredPlan === 'standard'}
                          onChange={handleChange}
                        />
                        <label 
                          htmlFor="standard" 
                          className="flex flex-col p-4 bg-white border border-neutral-200 rounded-lg cursor-pointer peer-checked:border-primary-500 peer-checked:bg-primary-50 hover:bg-neutral-50"
                        >
                          <span className="text-sm font-medium text-neutral-900">Standard Plan</span>
                          <span className="mt-1 text-xs text-neutral-500">$1,000 - $5,000</span>
                        </label>
                      </div>
                      <div className="relative">
                        <input 
                          type="radio" 
                          id="premium" 
                          name="preferredPlan" 
                          value="premium" 
                          className="sr-only peer" 
                          checked={formData.preferredPlan === 'premium'}
                          onChange={handleChange}
                        />
                        <label 
                          htmlFor="premium" 
                          className="flex flex-col p-4 bg-white border border-neutral-200 rounded-lg cursor-pointer peer-checked:border-primary-500 peer-checked:bg-primary-50 hover:bg-neutral-50"
                        >
                          <span className="text-sm font-medium text-neutral-900">Premium Plan</span>
                          <span className="mt-1 text-xs text-neutral-500">$6,000 - $10,000</span>
                        </label>
                      </div>
                      <div className="relative">
                        <input 
                          type="radio" 
                          id="advanced" 
                  name="preferredPlan"
                          value="advanced" 
                          className="sr-only peer" 
                          checked={formData.preferredPlan === 'advanced'}
                  onChange={handleChange}
                        />
                        <label 
                          htmlFor="advanced" 
                          className="flex flex-col p-4 bg-white border border-neutral-200 rounded-lg cursor-pointer peer-checked:border-primary-500 peer-checked:bg-primary-50 hover:bg-neutral-50"
                        >
                          <span className="text-sm font-medium text-neutral-900">Advanced Plan</span>
                          <span className="mt-1 text-xs text-neutral-500">$10,000 - $14,000</span>
                        </label>
                      </div>
                    </div>
              </div>
                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-medium text-neutral-700">Additional Information</label>
                <textarea
                  name="message"
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any specific questions about our investment plans?"
                      className="block w-full rounded-lg border-neutral-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50 transition-all"
                />
              </div>
                  <div className="pt-4">
                <motion.button
                  type="submit"
                      className="w-full inline-flex items-center justify-center px-6 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-secondary-500 hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 transition-colors duration-200 shadow-lg"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                >
                  Submit Investment Inquiry
                      <ArrowRightIcon className="ml-2 h-5 w-5" />
                </motion.button>
                {status.message && (
                  <div className={`mt-4 text-center font-medium ${status.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {status.message}
                  </div>
                )}
                <p className="text-xs text-center text-neutral-500 mt-3">
                  By submitting, you agree to our <a href="#" className="text-primary-600 hover:underline">Privacy Policy</a> and <a href="#" className="text-primary-600 hover:underline">Terms of Service</a>
                </p>
              </div>
            </motion.form>
          </div>
              
              {/* Testimonial */}
              <motion.div
                className="mt-8 bg-primary-50 rounded-xl p-6 border border-primary-100"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="h-12 w-12 rounded-full bg-primary-200 flex items-center justify-center text-primary-600 font-bold text-lg">
                      JP
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="h-5 w-5 text-secondary-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="mt-2 text-neutral-700">"The investment team provided exceptional guidance. Within just 3 weeks, my initial investment grew by 18x. Their responsive customer service made the entire process seamless."</p>
                    <p className="mt-2 font-medium text-primary-700">James Peterson, Premium Investor</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
          </div>
        </div>

      <Footer />
    </div>
  );
}

export default Contact; 